/**
 * useVqeTabDrill — the tab's subquery drill trail for the statement view:
 * its frames (the store keeps every tab's frames in one array), the
 * refresh-or-clear rule on buffer changes, breadcrumb jumps, and the drilled
 * statement + edit context the renderers mount against.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import { deriveSubqueryEditContext } from '../edit/deriveSubqueryEditContext';
import { extractStructureForContext, type VqeEditContext } from '../edit/editContext';
import { scopeSources } from '../render/common/outerQuerySources';
import { resolveInsertSourceBodyEnd } from '../render/insert/resolveInsertSourceBodyEnd';
import { makeNestedSubqueryStatement } from '../render/select/nestedStatement';
import { resolveDrilledSubqueryBodyEnd } from '../render/select/resolveDrilledSubqueryBodyEnd';
import { useVqeStore } from '../store/vqeStore';
import type { DrillEntry } from '../types';
import type { ClassificationResult } from './classifyTabQuery';

export type VqeTabDrill = {
  readonly stack: readonly DrillEntry[];
  readonly deepest: DrillEntry | null;
  readonly jumpTo: (depth: number) => void;
  readonly clear: () => void;
  /** The drilled body as a statement, when drilled into a SELECT / INSERT source. */
  readonly drilledStatement: StatementInfo | null;
  /** Edit context routed into the drilled body's byte range (null → top-level context). */
  readonly drilledEditContext: VqeEditContext | null;
  /** Aliases of the enclosing scope a drilled frame can reference (COM-34/35). */
  readonly outerAliases: readonly string[] | undefined;
};


export function useVqeTabDrill(
  tabId: string,
  classification: ClassificationResult | null,
  query: string,
  options: ParseOptions,
  editContext: VqeEditContext | null,
): VqeTabDrill {
  const stack = useVqeStore(useShallow((s) => s.drillStack.filter((entry) => entry.tabId === tabId)));
  const statement = classification?.statement ?? null;
  const mode = classification?.renderMode;
  const source = classification?.buffer.parsedDocument.source ?? query;
  const deepest = stack.length > 0 ? stack[stack.length - 1]! : null;

  const clear = useCallback(() => useVqeStore.getState().clearDrill(tabId), [tabId]);
  const jumpTo = useCallback(
    (depth: number) => {
      const store = useVqeStore.getState();
      if (depth === 0) {
        store.clearDrill(tabId);
        return;
      }
      for (let i = stack.length - depth; i > 0; i--) store.popDrill(tabId);
    },
    [tabId, stack.length],
  );

  // Body end of the deepest frame, re-derived from its stable bodyStart so an
  // inner edit that changed the body length never staleens the edit range. An
  // `'insert-source'` frame (an INSERT…SELECT's source, not paren-wrapped)
  // resolves via the INSERT outline anchored on the enclosing statement; every
  // other frame via the generic paren-depth-1 scan.
  const bodyEnd = (entry: DrillEntry | null): number | null =>
    entry == null
      ? null
      : entry.bodyShape === 'insert-source'
        ? statement
          ? resolveInsertSourceBodyEnd(source, statement.range, options)
          : null
        : resolveDrilledSubqueryBodyEnd(source, entry.bodyStart, options);

  // Refresh-or-clear on a buffer change: KEEP the drill while the deepest
  // bodyStart still resolves to a real subquery body; CLEAR only when an edit
  // invalidated it. Drilling itself doesn't change `query`, so push/pop never
  // re-trigger this.
  useEffect(() => {
    if (deepest && bodyEnd(deepest) == null) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, options]);

  const currentBodyEnd = bodyEnd(deepest);
  const drilledStatement =
    (mode === 'select' || mode === 'insert') && deepest && statement && currentBodyEnd != null
      ? makeNestedSubqueryStatement(statement.id, deepest.bodyStart, currentBodyEnd)
      : null;

  // The drilled renderer's edit affordances must route into the subquery's
  // byte range. Null (→ the top-level context) when the service buffer isn't
  // open yet (document open is async — resolveScope would throw).
  let drilledEditContext: VqeEditContext | null = null;
  if (drilledStatement && editContext && deepest && currentBodyEnd != null && statement) {
    try {
      drilledEditContext = deriveSubqueryEditContext(editContext, statement.id, deepest.bodyStart, currentBodyEnd);
    } catch {
      drilledEditContext = null;
    }
  }

  // COM-34/35 — a drilled frame renders as a fresh top-level SELECT, so its
  // correlated outer-scope tokens need the names of the enclosing sources the
  // body can actually reference: the same `outerQuerySources` the column
  // pickers list as "(outer query)" — every enclosing level by the engine's
  // scope rules (a CTE body sees none of its WITH statement's tables), minus
  // a name the body binds itself (`FROM orders o` inside hides an outer `o`).
  // Until the drilled edit context exists (the service buffer opens async),
  // nothing is marked — never the top-level aliases.
  const outerAliases = useMemo<readonly string[] | undefined>(() => {
    if (!drilledStatement || !drilledEditContext) return undefined;
    try {
      return scopeSources(drilledEditContext, extractStructureForContext(drilledEditContext)).outer.map((s) => s.exposedName);
    } catch {
      return undefined;
    }
    // Keyed on what the drilled body and its enclosing levels are made of: the
    // edit context is rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drilledStatement?.id, drilledEditContext == null, source, options]);

  return { stack, deepest, jumpTo, clear, drilledStatement, drilledEditContext, outerAliases };
}
